/**
 * Comparing people — the account area's organization page, for an owner or an
 * administrator.
 *
 * Said without ranking anybody: the range and the median of each figure, and
 * where each member stands only as a dot beside their own row, against the
 * median's tick. No order by performance (the table sorts by e-mail until the
 * reader asks otherwise), no colour per person, and "too few" rather than a rate
 * over fewer than three tasks.
 */
import { costPerLanded, formatPercent, formatUsd, successRate, type Totals } from './analysis';
import { MIN_SAMPLE, median, type Finding } from './findings';

export interface MemberRow extends Totals {
  member: string;
  /** How many installs sent this member's days in the period. */
  machines: number;
  /** When the last of them arrived, ISO — null when none did in the period. */
  lastSent: string | null;
  /** The model · effort with the most turns, or null with none. */
  mostly: string | null;
}

/** Where each member's two ratios sit on a line from 0 to the largest, and where the median does. */
export interface MemberSpread {
  rows: (MemberRow & { cpl: number | null; rate: number | null; few: boolean; cplX: number | null; rateX: number | null })[];
  cplMid: number | null;
  cplTop: number;
  rateMid: number | null;
  /** The median ticks, as 0–100 along each line. */
  cplMidX: number;
  rateMidX: number;
}

export function memberSpread(rows: readonly MemberRow[]): MemberSpread {
  const cpls = rows.map(costPerLanded).filter((v): v is number => v !== null);
  const rated = rows.filter((row) => row.started >= MIN_SAMPLE);
  const rates = rated.map((row) => successRate(row) ?? 0);
  const cplTop = Math.max(...cpls, 1_000_000);
  const cplMid = cpls.length ? median(cpls) : null;
  const rateMid = rates.length ? median(rates) : null;
  return {
    rows: rows.map((row) => {
      const cpl = costPerLanded(row);
      const few = row.started < MIN_SAMPLE;
      const rate = few ? null : successRate(row);
      return {
        ...row,
        cpl,
        rate,
        few,
        cplX: cpl !== null ? Math.round((cpl / cplTop) * 100) : null,
        rateX: rate !== null ? Math.round(rate * 100) : null
      };
    }),
    cplMid,
    cplTop,
    rateMid,
    cplMidX: cplMid !== null ? Math.round((cplMid / cplTop) * 100) : 0,
    rateMidX: rateMid !== null ? Math.round(rateMid * 100) : 0
  };
}

/** The spread across members, as sentences — range and median, never who is first. */
export function memberFindings(rows: readonly MemberRow[]): Finding[] {
  const out: Finding[] = [];
  const active = rows.filter((row) => row.turns > 0 || row.started > 0);
  if (active.length < 2) return out;
  const cpls = active.map(costPerLanded).filter((v): v is number => v !== null);
  if (cpls.length >= 2) {
    out.push({
      id: 'members-cost',
      group: 'know',
      subject: 'COST',
      text: `Cost per landed task ranges from ${formatUsd(Math.min(...cpls))} to ${formatUsd(Math.max(...cpls))} across ${cpls.length} members; the median is ${formatUsd(median(cpls))}.`,
      evidence: 'API list prices, every model and effort together.',
      stake: 0
    });
  }
  const rates = active.filter((row) => row.started >= MIN_SAMPLE).map((row) => successRate(row) ?? 0);
  if (rates.length >= 2) {
    out.push({
      id: 'members-success',
      group: 'know',
      subject: 'SUCCESS',
      text: `The share of started tasks that landed ranges from ${formatPercent(Math.min(...rates))} to ${formatPercent(Math.max(...rates))}; the median is ${formatPercent(median(rates))}.`,
      evidence: `Members with at least ${MIN_SAMPLE} tasks started: ${rates.length}.`,
      stake: 0
    });
  }
  const none = active.filter((row) => row.landed === 0 && row.started > 0).length;
  if (none) {
    out.push({
      id: 'members-none',
      group: 'know',
      subject: 'LANDED',
      text: `${none} member${none === 1 ? '' : 's'} started work that has not landed yet in this period.`,
      evidence: 'Work still under review counts as not landed until it merges.',
      stake: 0
    });
  }
  return out;
}
